import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendIcon from "@mui/icons-material/Send";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import AppButton from "../components/buttons/AppButton";

import FiscalizeInvoiceModal from "../components/fiscal/invoices/FiscalizeInvoiceModal";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import {
  getFiscalInvoices,
  resetFiscalServerStatus,
} from "../redux/actions/fiscalActions";

const FiscalInvoicesPage = () => {
  const dispatch = useDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .split("T")[0];
  const today = new Date().toISOString().split("T")[0];

  const {
    fiscalInvoices,
    fiscalInvoicePagination,
    loadingFiscalInvoices,
    errorLoadingFiscalInvoices,
    serverStatus,
  } = useSelector((state) => state.fiscal);

  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canRead = hasPermission("invoice:read_all");
  const canCreate = hasPermission("invoice:create");

  const fetchList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getFiscalInvoices({
        startDate,
        endDate,
        page: pageConfig.page,
        limit: pageConfig.limit,
      }),
    );
  }, [
    dispatch,
    startDate,
    endDate,
    pageConfig.page,
    pageConfig.limit,
    canRead,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Handle successful push auto-close
  useEffect(() => {
    if (
      serverStatus?.operation === "create-invoice" &&
      (serverStatus?.status === 200 || serverStatus?.status === 201)
    ) {
      setIsModalOpen(false);
      dispatch(resetFiscalServerStatus());
    }
  }, [serverStatus, dispatch]);

  const columns = useMemo(
    () => [
      {
        id: "traderSystemInvoiceNumber",
        label: "Local INV #",
        minWidth: 120,
        format: (val) => <b style={{ color: "#e81212" }}>{val}</b>,
      },
      { id: "cuReceiptNo", label: "eTIMS Receipt No", minWidth: 130 },
      { id: "customerName", label: "Customer", minWidth: 160 },
      {
        id: "invoiceDate",
        label: "KRA Date",
        minWidth: 140,
        format: (val) => new Date(val).toLocaleString(),
      },
      {
        id: "status",
        label: "KRA Status",
        minWidth: 110,
        format: (val) => <StatusChip status={val} />,
      },
      {
        id: "totalInvoiceAmount",
        label: "Total",
        minWidth: 110,
        align: "right",
        format: (val) =>
          Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="eTIMS Fiscal Invoices"
      subtitle="View, track, and manage invoices synchronized with the KRA eTIMS system."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            color="error"
            startIcon={<SendIcon />}
            onClick={() => setIsModalOpen(true)}
          >
            Push Invoice to eTIMS
          </AppButton>
        )
      }
    >
      <PageToolbar hideSearch>
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={fiscalInvoices}
        loading={loadingFiscalInvoices}
        error={errorLoadingFiscalInvoices}
        onRetry={fetchList}
        page={pageConfig.page}
        count={fiscalInvoicePagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={false}
        rowAction={(row) => (
          <Tooltip title="View eTIMS Details">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  navigateToTab(
                    `/fiscal/invoices/${row.traderSystemInvoiceNumber}`,
                  )
                }
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      />

      {isModalOpen && (
        <FiscalizeInvoiceModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </PageContainer>
  );
};
export default FiscalInvoicesPage;
